import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { useTheme, buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Text from 'Components/ComponentLibrary/Atoms/Text';
import Typography from 'Components/UI/Typography';
import { currency } from 'Helpers/Formats';
import CollapsibleCard from 'Components/ComponentLibrary/Components/Uncategorized/CollapsibleCard/CollapsibleCard';
import { Button } from 'Components/ComponentLibrary/ExternalLibraries';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import RichTextDisplay from 'Components/UI/RichTextDisplay';

const useStyles = buildUseStyles('FundingInstructionsCollapsibleCard', (theme) => ({
  root: {},
  details: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  action: {
    marginTop: theme.spacing(4),
    borderRadius: theme.radius.sm,
    fontWeight: theme.typography.fontWeightBold,
  },
  actionContent: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  carat: {
    color: theme.palette.facet.accentBlue19,
    transform: 'rotate(-90deg)',
  },
  field: {
    marginRight: theme.spacing(4),
  },
  body: {
    fontSize: theme.typography.fontSize1Rem,
    color: theme.palette.facet.primaryGray,
  },
}));

const FundingInstructionsCollapsibleCard = ({
  className,
  test,
  title,
  subtitle,
  amount,
  date,
  additionalInstructions,
  action,
  actionLabel,
  defaultExpanded,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);

  return (
    <CollapsibleCard
      className={classNames(className)}
      title={
        <Text
          className={classes.title}
          weight={'medium'}
          color={'primary'}
          variant={'h5'}
          align={'left'}
          text={title}
          test={test ? `${test}-title` : undefined}
        />
      }
      appearance={'blue'}
      subtitle={subtitle}
      defaultExpanded={defaultExpanded}
      test={test}
    >
      <Box className={classes.details}>
        {date && (
          <Box className={classes.field}>
            <Typography className={classes.label}>
              Date
            </Typography>
            <Typography className={classes.detailText} variant={'h5'} color={'primary'} weight={'medium'}>
              {date}
            </Typography>
          </Box>
        )}
        {amount && (
          <Box className={classes.field}>
            <Typography className={classes.label}>
              Amount
            </Typography>
            <Typography className={classes.detailText} variant={'h5'} color={'primary'} weight={'medium'}>
              {currency(parseFloat(amount), true)}
            </Typography>
          </Box>
        )}
      </Box>
      <Box className={classes.additionalInformation}>
        <Typography className={classes.label}>
          Additional Information
        </Typography>
        <RichTextDisplay content={additionalInstructions} />
      </Box>
      {action && (
        <Button
          className={classes.action}
          color={'primary'}
          variant={'contained'}
          onClick={action}
          fullWidth
        >
          <Box className={classes.actionContent}>
            <Typography variant={'h5'}>
              {actionLabel}
            </Typography>
            <ArrowDropDownIcon className={classes.carat} />
          </Box>
        </Button>
      )}
    </CollapsibleCard>
  );
};

FundingInstructionsCollapsibleCard.defaultProps = {
  className: '',
  test: undefined,
  title: 'Funding Instructions',
  subtitle: '',
  amount: undefined,
  date: '',
  action: undefined,
  actionLabel: 'Get Started',
  defaultExpanded: false,
};

FundingInstructionsCollapsibleCard.propTypes = {
  className: PropTypes.string,
  test: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  amount: PropTypes.number,
  date: PropTypes.string,
  additionalInstructions: PropTypes.string.isRequired,
  action: PropTypes.func,
  actionLabel: PropTypes.string,
  defaultExpanded: PropTypes.bool,
};

export default FundingInstructionsCollapsibleCard;
